import * as React from 'react';
import {Box, IconButton, Tooltip, useMediaQuery} from '@mui/material';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import PauseIcon from '@mui/icons-material/Pause';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';

type Props = {
    photoUrls: string[];
    intervalMs?: number;
    compact?: boolean;
};

// Auto-advancing slideshow for the kiosk page. Deliberately simple (no
// external carousel library) since it only needs to cycle through a handful
// of owner-provided image URLs with basic manual controls.
export function KioskPhotoCarousel({photoUrls, intervalMs = 10000, compact = false}: Props) {
    const [index, setIndex] = React.useState(0);
    const [paused, setPaused] = React.useState(false);
    const reducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)');

    React.useEffect(() => {
        if (index >= photoUrls.length) setIndex(0);
    }, [photoUrls.length, index]);

    React.useEffect(() => {
        if (photoUrls.length <= 1 || paused || reducedMotion) return;
        const timer = setInterval(() => {
            setIndex((i) => (i + 1) % photoUrls.length);
        }, intervalMs);
        return () => clearInterval(timer);
    }, [photoUrls.length, intervalMs, paused, reducedMotion]);

    if (photoUrls.length === 0) {
        return null;
    }

    const goTo = (newIndex: number) => setIndex((newIndex + photoUrls.length) % photoUrls.length);

    return (
        <Box sx={{width: '100%'}}>
            {/* Fixed-size frame: photos keep their aspect ratio (contain) and the
                frame's background shows through as borders where they don't fill it. */}
            <Box
                sx={{
                    position: 'relative',
                    aspectRatio: compact ? '16 / 10' : '4 / 3',
                    bgcolor: 'grey.900',
                    borderRadius: 2,
                    overflow: 'hidden',
                }}
            >
                <Box
                    component="img"
                    src={photoUrls[index]}
                    alt={`Kiosk photo ${index + 1} of ${photoUrls.length}`}
                    sx={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'contain',
                        display: 'block',
                    }}
                />

                {photoUrls.length > 1 && (
                    <>
                        <IconButton
                            aria-label="Previous photo"
                            onClick={() => goTo(index - 1)}
                            size="medium"
                            sx={{
                                position: 'absolute', top: '50%', left: 8, transform: 'translateY(-50%)',
                                bgcolor: 'rgba(255,255,255,0.8)', '&:hover': {bgcolor: 'rgba(255,255,255,0.95)'},
                            }}
                        >
                            <ArrowBackIosNewIcon fontSize="small"/>
                        </IconButton>
                        <IconButton
                            aria-label="Next photo"
                            onClick={() => goTo(index + 1)}
                            size="medium"
                            sx={{
                                position: 'absolute', top: '50%', right: 8, transform: 'translateY(-50%)',
                                bgcolor: 'rgba(255,255,255,0.8)', '&:hover': {bgcolor: 'rgba(255,255,255,0.95)'},
                            }}
                        >
                            <ArrowForwardIosIcon fontSize="small"/>
                        </IconButton>
                    </>
                )}
            </Box>

            {photoUrls.length > 1 && (
                <Box sx={{display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 0.25, mt: 0.5}}>
                    {photoUrls.map((_, i) => <IconButton key={i} aria-label={`Show photo ${i + 1}`} aria-pressed={i === index} onClick={() => goTo(i)} sx={{minWidth: 28, minHeight: 32, p: 1}}>
                        <Box sx={{width: 6, height: 6, borderRadius: '50%', bgcolor: i === index ? 'primary.main' : '#A2B1A8'}}/>
                    </IconButton>)}
                    {!reducedMotion && <Tooltip title={paused ? 'Play slideshow' : 'Pause slideshow'}><IconButton aria-label={paused ? 'Play slideshow' : 'Pause slideshow'} onClick={() => setPaused((value) => !value)}>{paused ? <PlayArrowIcon fontSize="small"/> : <PauseIcon fontSize="small"/>}</IconButton></Tooltip>}
                </Box>
            )}
        </Box>
    );
}
